import type { QueryClient } from '@tanstack/react-query';

import { hasSession, isUnauthorizedError, refreshAccessToken } from '@/shared/api';
import { useSessionStore } from '@/shared/store/sessionStore';

import { accountKeys, authApi } from '../api/authApi';

/**
 * The silent session restore that runs once before any protected screen paints.
 *
 * WHY THIS HAS TO EXIST. The access token is deliberately memory-only, so after
 * a reload the client holds nothing at all — but the refresh token is an
 * httpOnly cookie the browser still has. Without this step every hard refresh
 * would read as "signed out" and throw the user back to /welcome mid-task, and
 * gating the app (which is the point of this pass) would make that failure
 * total rather than cosmetic.
 *
 * Two calls, in order:
 *
 *  1. `refreshAccessToken()` — mints an access token from the cookie. Skipped
 *     when one is already in memory. This is single-flighted inside the client,
 *     so concurrent callers share one request.
 *  2. `/account/me` — proves the token works AND is the only source of the
 *     account id and email the shell needs. It goes through `fetchQuery` rather
 *     than `authApi.me()` directly so the result lands in the query cache: the
 *     profile screen and the share-id screen then read it without a second
 *     round-trip.
 *
 * The three outcomes are kept apart on purpose:
 *
 *  - 401 anywhere → there is genuinely no session. `sessionAbsent()`.
 *  - anything else (offline, timeout, 5xx, the refresh endpoint itself rate
 *    limited) → we do not know. `sessionCheckFailed()`, and the gate says so on
 *    screen with a retry. Treating this as "signed out" would sign people out
 *    every time their train enters a tunnel.
 *  - success → `sessionRestored()`.
 */
export async function restoreSession(queryClient: QueryClient): Promise<void> {
  const store = useSessionStore.getState();

  try {
    if (!hasSession()) await refreshAccessToken();

    const profile = await queryClient.fetchQuery({
      queryKey: accountKeys.me(),
      queryFn: () => authApi.me()
    });

    store.sessionRestored({ id: String(profile.id), email: profile.email });
  } catch (error) {
    // A failed refresh has already cleared the token store and emitted
    // `expired`, which sets `expiredReason` — right for a session that died
    // under an active user, wrong for a visitor who simply never had one. The
    // explicit call below wins because it lands after the event.
    if (isSessionRefused(error)) store.sessionAbsent();
    else store.sessionCheckFailed();
  }
}

/**
 * "The server told us there is no session", as opposed to "we could not ask".
 *
 * The distinction is finer than `isUnauthorizedError` alone, because the client
 * raises `UnauthorizedError` for ANY unsuccessful refresh — including a 503 or a
 * gateway error, where the truthful answer is that we do not know. Only a real
 * 401/403 is an answer; everything else is a failure to get one, and treating
 * the two the same is how a wobbly connection turns into a forced sign-out.
 */
function isSessionRefused(error: unknown): boolean {
  if (!isUnauthorizedError(error)) return false;
  return error.status === undefined || error.status === 401 || error.status === 403;
}

/**
 * One restore per page load, shared by every caller.
 *
 * Several protected routes can mount their gate in the same tick (a redirect,
 * a re-render under StrictMode's double-invoke); without this they would each
 * start their own restore and race two refreshes at a rotating refresh token.
 */
let inFlight: Promise<void> | null = null;

export function restoreSessionOnce(queryClient: QueryClient): Promise<void> {
  inFlight ??= restoreSession(queryClient).finally(() => {
    inFlight = null;
  });
  return inFlight;
}
